import { HEXColor } from '@neptune/colors-domain';

import { blendRgbaColors, hexToRgbParts, rgbStringToRgbParts } from 'common/color';

import { HeatmapSeriesData, NullableColor } from './heatmap-data';
import { HistogramColumnData, HistogramSeries } from './histogram-data';

type RGBA = { r: number; g: number; b: number; a: number };

export const generateHeatmapData = (params: {
  series: HistogramSeries[];
  maxValuePerSeries: Record<string, number>;
  xValues: number[];
  edgesWithinRange: number[];
  columnsData: HistogramColumnData[];
  firstEdgeIndex: number;
  lastEdgeIndex: number;
}): HeatmapSeriesData => {
  const { series, maxValuePerSeries, xValues } = params;
  const { edgesWithinRange, columnsData, firstEdgeIndex, lastEdgeIndex } = params;

  const binColors = generateBinColors({
    series,
    edgesWithinRange,
    columnsData,
    firstEdgeIndex,
    lastEdgeIndex,
    maxValuePerSeries,
  });

  return [xValues, edgesWithinRange, binColors];
};

const resolveValue = (params: {
  xIdx: number;
  yIdx: number;
  columnsData: HistogramColumnData[];
  entityId: string;
  firstEdgeIndex: number;
  lastEdgeIndex: number;
}) => {
  const { xIdx, yIdx, columnsData, entityId, firstEdgeIndex, lastEdgeIndex } = params;

  const seriesValues = columnsData[xIdx].series[entityId]?.values;

  if (!seriesValues) {
    return undefined;
  }

  const idx = firstEdgeIndex + yIdx;

  if (idx < firstEdgeIndex || idx >= lastEdgeIndex) {
    return undefined;
  }

  return seriesValues[idx];
};

const generateBinColors = (params: {
  series: HistogramSeries[];
  edgesWithinRange: number[];
  columnsData: HistogramColumnData[];
  firstEdgeIndex: number;
  lastEdgeIndex: number;
  maxValuePerSeries: Record<string, number>;
}): NullableColor[] => {
  const {
    series,
    edgesWithinRange,
    columnsData,
    firstEdgeIndex,
    lastEdgeIndex,
    maxValuePerSeries,
  } = params;

  return series.reduce<NullableColor[]>((binColors, { entityId, color }) => {
    for (let yIdx = 0; yIdx < edgesWithinRange.length - 1; yIdx++) {
      for (let xIdx = 0; xIdx < columnsData.length; xIdx++) {
        const value = resolveValue({
          xIdx,
          yIdx,
          columnsData,
          entityId,
          firstEdgeIndex,
          lastEdgeIndex,
        });
        const maxValue = maxValuePerSeries[entityId] ?? -Infinity;
        const binColorIndex = yIdx * columnsData.length + xIdx;
        const seriesBinColor = value ? calculateSingleBinColor(value, maxValue, color) : null;

        const currentBinColor = binColors[binColorIndex];

        if (!currentBinColor) {
          binColors[binColorIndex] = seriesBinColor && rgbaStructureToRgaString(seriesBinColor);
          continue;
        } else if (!seriesBinColor) {
          continue;
        }

        const currentBinColorInRgba = rgbaStringToRgbaStructure(currentBinColor);
        const { r, g, b, a } = blendRGBA(seriesBinColor, currentBinColorInRgba);
        binColors[binColorIndex] = `rgba(${r}, ${g}, ${b}, ${a})`;
      }
    }

    return binColors;
  }, []);
};

const MAX_OPACITY = 0.6;
const MIN_OPACITY = 0.1;

const calcBinOpacity = (value: number, maxValue: number): number => {
  // The opacity varies linearly between MIN_OPACITY and MAX_OPACITY.
  // If the value is very small*, the opacity will be equal to MIN_OPACITY.
  // If the value is equal to maxValue, the opacity is equal MAX_OPACITY.
  // * if the value is zero, this function doesn't even get called and the opacity is zero
  return (value / maxValue) * (MAX_OPACITY - MIN_OPACITY) + MIN_OPACITY;
};

const calculateSingleBinColor = (value: number, maxValue: number, seriesColor: HEXColor): RGBA => {
  const opacity = calcBinOpacity(value, maxValue);
  const rgbParts = hexToRgbParts(seriesColor);

  if (!rgbParts) {
    throw new Error(`Invalid color: ${seriesColor}`);
  }

  const [r, g, b] = rgbParts;

  return { r, g, b, a: opacity };
};

const rgbaStringToRgbaStructure = (rgba: string): RGBA => {
  const rgbaParts = rgbStringToRgbParts(rgba);

  if (!rgbaParts) {
    throw new Error(`Invalid color: ${rgba}`);
  }

  const [r, g, b, a = 1] = rgbaParts;

  return { r, g, b, a };
};

function blendRGBA(fg: RGBA, bg: RGBA): RGBA {
  const [r, g, b, a] = blendRgbaColors([fg.r, fg.g, fg.b, fg.a], [bg.r, bg.g, bg.b, bg.a]);

  return { r, g, b, a };
}

const rgbaStructureToRgaString = (rgba: RGBA): string => {
  const { r, g, b, a } = rgba;

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
